import { AddIcon } from "@chakra-ui/icons";
import { Button, VStack } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LibraryElementTypeDto, type LibraryElementContentDto, type SelectionCriterionDto } from "../../../autogen/bff-api";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { QualificationRequirementList } from "../../sourcing-events/buyer/wizard/components/QualificationRequirementList";
import { QualificationModal, type QualificationFormValues } from "../kravbibliotek/QualificationModal";
import { AddedFromLibraryBanner } from "../../../common/library/AddedFromLibraryBanner";
import { AddRequirementPanel } from "../../../common/library/AddRequirementPanel";
import { useAddFromLibrary } from "../../../common/library/useAddFromLibrary";
import { SectionEmptyState } from "../../../common/library/SectionEmptyState";
import { TemplateSectionCard } from "./TemplateSectionCard";

// t('Qualification requirements')
// t("Requirements for the supplier's suitability (economy, technical capacity, quality and environment).")
// t('Delete requirement') t('Are you sure you want to delete this requirement?') t('Delete') t('Add')
// t('Add qualification requirements') t('qualification requirements')
// t('No qualification requirements in this template')

interface Props {
  items: SelectionCriterionDto[];
  onChange: (items: SelectionCriterionDto[]) => void;
}

type ModalState = { mode: "edit"; index: number; initialValues: QualificationFormValues };

export const TemplateQualificationSection = ({ items, onChange }: Props) => {
  const { t } = useTranslation();
  const confirmationModal = useConfirmationModal();
  const [modal, setModal] = useState<ModalState | null>(null);
  const [modalKey, setModalKey] = useState(0);
  const library = useAddFromLibrary({ items, commit: onChange });
  const { save } = library;



  const openEdit = (index: number) => {
    const item = items[index];
    setModalKey((k) => k + 1);
    setModal({
      mode: "edit",
      index,
      initialValues: {
        type: item.type,
        name: item.name,
        description: item.description,
        requiresEvidence: item.requiresEvidence,
      },
    });
  };

  const handleSubmit = async (values: SelectionCriterionDto) => {
    if (modal) save(items.map((it, i) => (i === modal.index ? values : it)));
    setModal(null);
  };

  const handleDelete = (index: number) => {
    confirmationModal.show({
      title: t("Delete requirement"),
      description: t("Are you sure you want to delete this requirement?"),
      confirmButtonText: t("Delete") ?? "",
      confirmButtonColor: "red",
      onConfirmed: () => save(items.filter((_, i) => i !== index)),
    });
  };

  const addFromLibrary = (contents: LibraryElementContentDto[]) =>
    library.add(contents.flatMap((c) => (c.selection ? [c.selection] : [])));

  return (
    <TemplateSectionCard
      title={t("Qualification requirements")}
      count={items.length}
      description={t("Requirements for the supplier's suitability (economy, technical capacity, quality and environment).")}
    >
      <VStack align="stretch" spacing="4">
        {library.lastAdded && <AddedFromLibraryBanner count={library.lastAdded.count} onUndo={library.undo} />}
        {items.length === 0 ? (
          <SectionEmptyState
            title={t("No qualification requirements in this template")}
            type={LibraryElementTypeDto.Selection}
            onAddFromLibrary={() => library.openPanel("library")}
            onWriteNew={() => library.openPanel("new")}
          />
        ) : (
          <>
            <QualificationRequirementList
              items={items}
              onAdd={() => library.openPanel("library")}
              onEdit={openEdit}
              onDelete={handleDelete}
              markedCount={library.markedCount}
            />
            <Button
              variant="outline"
              size="sm"
              leftIcon={<AddIcon />}
              onClick={() => library.openPanel("library")}
              alignSelf="flex-start"
            >
              {t("Add")}
            </Button>
          </>
        )}
      </VStack>

      <AddRequirementPanel
        isOpen={library.isPanelOpen}
        onClose={library.closePanel}
        type={LibraryElementTypeDto.Selection}
        title={t("Add qualification requirements")}
        typeNoun={t("qualification requirements")}
        isAlreadyInSection={(el) => items.some((i) => i.name === el.content.selection?.name)}
        initialTab={library.tab}
        onAdd={addFromLibrary}
      />

      <QualificationModal
        key={modalKey}
        isOpen={modal !== null}
        isSaving={false}
        mode="edit"
        defaultValues={modal?.initialValues}
        onSubmit={handleSubmit}
        onClose={() => setModal(null)}
      />
      {confirmationModal.modal}
    </TemplateSectionCard>
  );
};
